<template>
  <div class="ps-6">
    <v-row class="ma-0 pa-0 my-3">
      <v-col class="ma-0 pa-0">
        <div class="d-flex align-center" v-if="showGroup">
          <Group
            :groups="groups"
            :title="title"
            @manageGroups="$emit('manageGroups')"
            @createGroup="$emit('createGroup')"
            @apply="handleGroupApply"
          />
          <p
            class="ms-4 ma-0 pa-0 font-size-14 font-weight-500 blue-grey--text text--darken-2"
          >
            {{
              $t('organization_structure.items_selectedWithCount', {
                count: selected.length,
              })
            }}
          </p>
        </div>
      </v-col>
      <v-col class="d-flex justify-end ma-0 pa-0">
        <ColumnsSelector :headers="headers" @toggleColumn="onToggleColumn" />
      </v-col>
    </v-row>
    <v-row class="ma-0 pa-0">
      <v-col cols="12" class="ma-0 pa-0">
        <v-data-table
          id="data-structure-table"
          data-private
          v-model="selected"
          mobile-breakpoint="0"
          :items="items"
          :headers="columns"
          :loading="loading"
          :options.sync="options"
          :show-select="bulkActions"
          :server-items-length="totalItems"
          :footer-props="footerOptions"
          @input="$emit('selectItems', $event)"
        >
          <template
            v-slot:item.data-table-select="{ item, isSelected, select }"
          >
            <v-checkbox
              class="ma-0 pa-0"
              color="blue"
              hide-details
              :class="{ disabled: !item.isEnabled }"
              :input-value="isSelected"
              :disabled="!item.isEnabled"
              :ripple="false"
              @change="select($event)"
            />
          </template>
          <template v-slot:item.name="{ item }">
            <div class="d-flex align-center name">
              <p
                :class="{ disabled: !item.isEnabled }"
                class="ma-0 pa-0 blue-grey--text text--darken-3 font-weight-400 font-size-14"
              >
                {{ item.name.trim() }}
              </p>

              <v-tooltip right>
                <template v-slot:activator="{ on }">
                  <v-icon
                    v-on="on"
                    color="blue-grey"
                    class="ml-2 copy-icon"
                    small
                    @click="copyItemId(item.id)"
                    >mdi-content-copy</v-icon
                  >
                </template>
                {{ $t('common.copy_id') }}
              </v-tooltip>
            </div>
          </template>

          <template v-slot:item.country="{ item }">
            <p
              :class="{ disabled: !item.isEnabled }"
              class="ma-0 pa-0 blue-grey--text text--darken-3 font-weight-400 font-size-14"
            >
              {{ item.country }}
            </p>
          </template>
          <template v-slot:item.sites="{ item }">
            <div v-if="hasSites" :class="{ disabled: !item.isEnabled }">
              <ChipList
                v-if="Boolean(item.sites.length)"
                outlined
                class="py-1"
                :items="getNames(item.sites)"
                :limit="3"
                :totalItems="getTotalSites(item)"
                :menuItems="getAllSites(item)"
              />

              <v-chip
                small
                outlined
                v-else
                color="blue-grey lighten-2"
                class="caption blue-grey--text text--darken-3"
              >
                <v-icon size="15" color="blue-grey darken-1" class="mr-1"
                  >mdi-web</v-icon
                >
                {{ $t('common.global') }}
              </v-chip>
            </div>
          </template>
          <template v-slot:item.sitesSample="{ item }">
            <div v-if="hasSites" :class="{ disabled: !item.isEnabled }">
              <ChipList
                v-if="Boolean(item.sitesSample.length)"
                outlined
                class="py-1"
                :items="getNames(item.sitesSample)"
                :limit="3"
                :totalItems="getTotalSites(item)"
                :menuItems="getAllSites(item)"
              />
              <p
                v-else
                class="caption blue-grey--text text--darken-3 ma-0 pa-0"
              >
                {{ $t('organization_structure.no_sites') }}
              </p>
            </div>
          </template>
          <template v-slot:item.groups="{ item }">
            <div v-if="hasGroups" :class="{ disabled: !item.isEnabled }">
              <ChipList
                v-if="Boolean(item.groups.length)"
                outlined
                :items="getNames(item.groups)"
                :limit="2"
                :totalItems="getTotalGroups(item)"
                :menuItems="getAllSiteGroups(item)"
              />
              <p
                v-else
                class="ma-0 pa-0 caption blue-grey--text text--darken-3"
              >
                {{ $t('organization_structure.no_group_other') }}
              </p>
            </div>
          </template>
          <template v-slot:item.tags="{ item }">
            <div v-if="hasTags" :class="{ disabled: !item.isEnabled }">
              <ChipList
                v-if="Boolean(item.tagsSample.length)"
                outlined
                class="py-1"
                :items="getNames(item.tagsSample)"
                :limit="3"
                :totalItems="getTotalTags(item)"
                :menuItems="getAllTags(item)"
              />
              <p
                v-else
                class="caption blue-grey--text text--darken-3 ma-0 pa-0"
              >
                {{ $t('organization_structure.no_tags') }}
              </p>
            </div>
          </template>
          <template v-slot:item.actions="{ item }">
            <div class="d-flex justify-center">
              <template v-if="showGoToSettings">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <div v-on="on" class="me-2">
                      <v-icon
                        color="blue-grey"
                        @click="$emit('goToSettings', item)"
                        >mdi-cog-outline</v-icon
                      >
                    </div>
                  </template>
                  <span>{{ $t('site_settings.go_to_site_settings') }}</span>
                </v-tooltip>
              </template>

              <v-icon color="blue-grey" @click="$emit('edit', item)">
                mdi-pencil-outline
              </v-icon>
            </div>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import ColumnsSelector from '@/components/ColumnsSelector.vue'
import Group from './Group.vue'
import ChipList from '@/components/ChipList.vue'

import { mapGetters } from 'vuex'

import mixin from '@/mixins/mixin.vue'

const DEFAULT_SIZE = 100

export default {
  name: 'StructureTable',

  mixins: [mixin],

  components: {
    ColumnsSelector,
    Group,
    ChipList,
  },

  props: {
    bulkActions: Boolean,
    items: { type: Array, required: true },
    loading: Boolean,
    totalItems: { Number },
    headers: { type: Array, required: true },
    groups: { type: Array, required: false },
    title: { type: String, required: false },
  },

  data() {
    return {
      footerOptions: {
        itemsPerPageOptions: [20, 50, 100],
      },
      selected: [],
      currentPage: 1,
      itemsPerPage: DEFAULT_SIZE,
    }
  },

  computed: {
    ...mapGetters([
      'organizationSites',
      'organizationTagGroups',
      'organizationTags',
      'organizationTagGroups',
    ]),

    hasSites() {
      return Boolean(this.items?.[0]?.sites)
    },

    hasTags() {
      return Boolean(this.items?.[0]?.tags)
    },

    hasGroups() {
      return Boolean(this.items?.[0]?.groups)
    },

    columns() {
      return this.headers.filter(h => h.show)
    },

    showGroup() {
      return Boolean(this.selected?.length)
    },

    showGoToSettings() {
      return this.items?.[0]?.actions?.includes?.('goToSettings')
    },

    options: {
      get() {
        return {
          page: this.currentPage,
          itemsLength: this.totalTags,
          itemsPerPage: this.itemsPerPage,
          multiSort: false,
          mustSort: true,
        }
      },
      set(event) {
        this.itemsPerPage = event.itemsPerPage
        this.$emit('getItems', {
          page: event.page,
          itemsPerPage: event.itemsPerPage,
          sortDesc: event.sortDesc[0],
          sortBy: event.sortBy[0],
        })
      },
    },
  },

  methods: {
    getAllSites(item) {
      const sites =
        item?.sites?.[0]?.id ?? item?.sites?.[0]?._id
          ? item.sites
          : this.organizationSites?.filter(site =>
              item?.sites?.some(siteId => site.id === siteId)
            )

      return this.getNames(sites)
    },

    getAllSiteGroups(item) {
      const siteGroups = item?.groups?.[0].id
        ? item.groups
        : this.organizationSiteGroups?.filter(group =>
            item?.groups?.some(groupId => group.id === groupId)
          )

      return this.getNames(siteGroups)
    },

    getAllTagGroups(item) {
      const tagGroups = item?.groups?.[0].id
        ? item.groups
        : this.organizationTagGroups?.filter(group =>
            item?.groups?.some(groupId => group.id === groupId)
          )

      return this.getNames(tagGroups)
    },

    getAllTags(item) {
      const tags = item?.tags?.[0].id
        ? item.tags
        : this.organizationTags?.filter(tag =>
            item?.tags?.some(tagId => tag.id === tagId)
          )
      return this.getNames(tags)
    },

    getTotalSites(item) {
      return item?.sites?.length ?? 0
    },

    getTotalTags(item) {
      return item?.tags?.length ?? 0
    },

    getTotalGroups(item) {
      return item?.groups?.length ?? 0
    },

    getNames(items) {
      return items.map(item => item.name)
    },

    handleGroupApply(group) {
      const selectedIds = this.selected
        .filter(item => item.isEnabled)
        .map(item => item.id)

      this.$emit('apply', { group, selectedIds })
    },

    onToggleColumn({ index, value }) {
      this.headers[index].show = value
    },

    copyItemId(id) {
      navigator.clipboard.writeText(id)
      this.showSuccess(this.$t('common.copy_id_success'))
    },
  },
}
</script>
<style scoped lang="scss">
.disabled {
  opacity: 0.3;
}

.name {
  .copy-icon {
    visibility: hidden;
  }

  &:hover {
    .copy-icon {
      visibility: visible;
    }
  }
}
</style>
